import React, { useMemo, useState } from 'react';
import { useTheme } from '@emotion/react';
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import AppleHealthChartTooltip from 'pages/Dashboard/components/AppleHealthChartTooltip';
import ChartAxisLabel from 'pages/Dashboard/components/ChartAxisLabel';
import { getStartOfDayLocal } from 'utils/dateUtils';
import Theme from 'theme';
import { DataKey } from 'pages/Dashboard/components/AppleHealthActivitySummaryChart';
import { WaypointTracking } from 'pages/Dashboard/types/waypoints.types';

interface MiniAppleHealthChartProps {
  chartData:WaypointTracking[]
  dataKey: DataKey;
  units: string;
  yAxisProps: {
    startTime: number;
    endTime: number;
    ticks: number[];
    isInDays: boolean;
  };
  barSize?: number;
  barColor?: string;
  averageValue?: number;
  sourceOptions: { label: string; value: string; isSelected: boolean }[];
  setSourceId: (sourceId: string) => void;
  sourceId:string;
}

function CustomTooltip({
  active,
  payload,
  label,
  units,
  dataKey,
  dataLabel,
  isInDays,
}: {
  active?: boolean;
  payload?: Payload<string | number | (string | number)[], string | number>[] | undefined;
  label?: number;
  units: string;
  dataKey: string;
  dataLabel: string;
  isInDays: boolean;
}) {
  const data = [{
    key: dataKey,
    label: dataLabel,
    color: Theme.custom.colors.primaryMain,
    units,
    value: payload?.[0]?.payload?.[dataKey] ?? 0,
  }];

  return (
    <AppleHealthChartTooltip
      data={data}
      isInDays={isInDays}
      active={active}
      label={label}
      payload={payload}
    />
  );
}

function MiniAppleHealthChart({
  chartData,
  dataKey,
  units,
  yAxisProps: { ticks, startTime, endTime, isInDays },
  barSize = 4,
  barColor,
  averageValue,
  sourceOptions,
  setSourceId,
  sourceId,
} :MiniAppleHealthChartProps) {
  const [focusBar, setFocusBar] = useState(-1);
  const theme = useTheme();

  const data = chartData.map((w) => {
    const timestamp = (w.timestamp ?? 0) * 1000;
    const date = getStartOfDayLocal(timestamp, isInDays);
    const value = parseInt(w?.absoluteValue ?? '0', 10);

    return {
      date,
      [dataKey]: value,
    };
  });

  const label = useMemo(() => {
    switch (dataKey) {
      case DataKey.ActiveEnergyBurned:
        return 'Active energy';
      case DataKey.DaylightExposure:
        return 'Time';
      case DataKey.TotalCaloriesBurned:
        return 'Total energy';
      case DataKey.Steps:
        return 'Steps';
      default:
        return 'Score';
    }
  }, [dataKey]);
  const handleChange = (event: SelectChangeEvent) => {
    setSourceId(event.target.value as string);
  };
  return (

    <Grid container width={330} height={350}>
      {sourceOptions.length > 1 && (
        <FormControl sx={{ mb: 1, flexGrow: 1 }}>
          <InputLabel>Data source</InputLabel>
          <Select value={sourceId} label='Data source' onChange={handleChange} className='fs-mask'>
            {sourceOptions.map((s) => (
              <MenuItem key={s.value} value={s.value} className='fs-mask'>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {sourceOptions.length === 1 && (
        <Typography color={Theme.custom.colors.lightTextSecondary} sx={{ mb: 1 }} className='fs-mask'>
          Source:
          {' '}
          {sourceOptions[0].label}
        </Typography>
      )}
      <Box width={320} height={280}>
        <ResponsiveContainer>
          <BarChart
            data={data}
            barSize={barSize}
            onMouseMove={(state) => setFocusBar(state?.isTooltipActive
              ? state.activeTooltipIndex ?? -1 : -1)}
            onMouseLeave={() => setFocusBar(-1)}
            margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <XAxis
              dataKey='date'
              type='number'
              domain={[startTime, endTime]}
              tick={<ChartAxisLabel isInDays={isInDays} />}
              tickFormatter={() => ''}
              ticks={ticks}
              height={30}
              tickLine={false}
              tickSize={2}

            />
            <YAxis
              width={28}
              orientation='left'
              fontSize={9}
              tickFormatter={(v) => `${v} ${units.toLowerCase()}`}
              tickLine={false}
              tickSize={2}
            />
            <CartesianGrid />
            <Tooltip
              content={(
                <CustomTooltip
                  units={units}
                  dataKey={dataKey}
                  dataLabel={label}
                  isInDays={isInDays}
                />
            )}
              cursor={false}
            />
            <Bar dataKey={dataKey} fill={barColor ?? theme.custom.colors.primaryMain}>
              {data.map((entry, index) => (
                <Cell
                  fill={
                  focusBar !== index
                    ? barColor ?? theme.custom.colors.primaryMain
                    : `${barColor ?? theme.custom.colors.primaryMain}aa`
                }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Grid>
  );
}

export default MiniAppleHealthChart;
